import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthWrapper';
import { Menu, X, LogOut, ChevronDown } from 'lucide-react';

const logo = '/elinnos-logo.png';

/**
 * Shared header — identical to the landing page header.
 * Used on ALL pages for consistent navigation.
 * Props: activePage = 'features' | 'platform' | 'dashboard' | 'pricing' | 'contact' | 'download' | 'billing'
 */
const SharedHeader = ({ activePage }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const userMenuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const scrollToSection = (sectionId) => {
    navigate(`/?section=${sectionId}`);
  };

  const handleSignIn = () => navigate('/dashboard');
  const handleLogout = () => { logout(); navigate('/'); };

  const navLink = (label, onClick, isActive) => (
    <button
      type="button"
      onClick={onClick}
      className="nav-tab"
      style={isActive ? { color: '#22d3ee' } : {}}
    >
      {label}
    </button>
  );

  return (
    <nav className="fixed top-0 w-full header-ref-nav z-50 transition-colors">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-14 sm:h-16">
          {/* Left Brand */}
          <div className="flex items-center space-x-3">
            {logo ? (
              <a href="/" className="hover:opacity-80 transition-opacity flex items-center gap-2">
                <img src={logo} alt="Elinnos" className="h-12 w-auto" />
              </a>
            ) : (
              <a href="/" className="text-xl font-extrabold tracking-tight text-white hover:text-cyan-400 transition-colors">
                elinnos
              </a>
            )}
            <span className="text-slate-600 font-light text-lg">|</span>
            <a href="/" className="text-base sm:text-lg font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400 hover:opacity-80 transition-opacity">
              chipdesigner.ai
            </a>
          </div>

          {/* Center Nav Links */}
          <div className="hidden md:flex items-center space-x-8">
            {navLink('Features', () => scrollToSection('features'), activePage === 'features')}
            {navLink('Platform', () => scrollToSection('demo'), activePage === 'platform')}
            {navLink('Dashboard', () => navigate('/dashboard'), activePage === 'dashboard')}
            {navLink('Pricing', () => navigate('/pricing'), activePage === 'pricing')}
            {navLink('Contact', () => scrollToSection('contact'), activePage === 'contact')}
            {navLink('Download', () => navigate('/downloads'), activePage === 'download')}
          </div>

          {/* Right User Actions */}
          <div className="hidden md:flex items-center space-x-4">
            {user ? (
              <div className="relative" ref={userMenuRef}>
                <button
                  type="button"
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center gap-2.5 px-3 py-1.5 rounded-full bg-slate-800/80 hover:bg-slate-700/80 border border-slate-700/60 transition-all cursor-pointer"
                >
                  <div className="w-7 h-7 rounded-full bg-gradient-to-tr from-blue-600 to-cyan-400 flex items-center justify-center text-white font-bold text-xs shadow-sm">
                    {user?.name?.charAt(0).toUpperCase() || 'P'}
                  </div>
                  <ChevronDown className={`w-3.5 h-3.5 text-slate-400 transition-transform ${userMenuOpen ? 'rotate-180' : ''}`} />
                </button>

                {userMenuOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-slate-900/95 backdrop-blur-xl rounded-xl shadow-2xl border border-slate-700/60 py-2 z-50">
                    <div className="px-4 py-2 border-b border-slate-800">
                      <p className="text-sm font-semibold text-white">{user?.name || 'User'}</p>
                      <p className="text-xs text-slate-400 truncate">{user?.email || ''}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => { navigate('/dashboard'); setUserMenuOpen(false); }}
                      className="w-full flex items-center gap-2 px-4 py-2 text-sm text-slate-300 hover:bg-slate-800 transition-colors cursor-pointer text-left"
                      style={{ background: 'none', border: 'none', font: 'inherit' }}
                    >
                      Dashboard
                    </button>
                    <button
                      type="button"
                      onClick={() => { navigate('/pricing'); setUserMenuOpen(false); }}
                      className="w-full flex items-center gap-2 px-4 py-2 text-sm text-slate-300 hover:bg-slate-800 transition-colors cursor-pointer text-left"
                      style={{ background: 'none', border: 'none', font: 'inherit' }}
                    >
                      Pricing
                    </button>
                    <button
                      type="button"
                      onClick={handleLogout}
                      className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-400 hover:bg-red-500/10 transition-colors cursor-pointer text-left"
                      style={{ background: 'none', border: 'none', font: 'inherit' }}
                    >
                      <LogOut className="w-4 h-4" />
                      Logout
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <button
                type="button"
                onClick={handleSignIn}
                className="bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-500 hover:to-cyan-400 text-white px-5 py-2 rounded-lg text-sm font-medium shadow-[0_0_15px_rgba(6,182,212,0.3)] transition-all cursor-pointer"
                style={{ border: 'none' }}
              >
                Sign In
              </button>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              type="button"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="text-slate-300 hover:text-white"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu Dropdown */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-slate-800/60 bg-[#0a0e1a]/95 backdrop-blur-xl">
            <div className="px-4 py-4 space-y-1">
              <button type="button" onClick={() => { scrollToSection('features'); setMobileMenuOpen(false); }} className="block w-full text-left py-2.5 text-slate-300 hover:text-white transition-colors">Features</button>
              <button type="button" onClick={() => { scrollToSection('demo'); setMobileMenuOpen(false); }} className="block w-full text-left py-2.5 text-slate-300 hover:text-white transition-colors">Platform</button>
              <button type="button" onClick={() => { navigate('/dashboard'); setMobileMenuOpen(false); }} className="block w-full text-left py-2.5 text-slate-300 hover:text-white transition-colors">Dashboard</button>
              <button type="button" onClick={() => { navigate('/pricing'); setMobileMenuOpen(false); }} className="block w-full text-left py-2.5 text-slate-300 hover:text-white transition-colors">Pricing</button>
              <button type="button" onClick={() => { scrollToSection('contact'); setMobileMenuOpen(false); }} className="block w-full text-left py-2.5 text-slate-300 hover:text-white transition-colors">Contact</button>
              <button type="button" onClick={() => { navigate('/downloads'); setMobileMenuOpen(false); }} className="block w-full text-left py-2.5 text-slate-300 hover:text-white transition-colors">Download</button>
              <div className="pt-3 border-t border-slate-800/60">
                {user ? (
                  <button type="button" onClick={() => { handleLogout(); setMobileMenuOpen(false); }} className="flex items-center gap-2 py-2.5 text-red-400 hover:text-red-300 transition-colors">
                    <LogOut className="w-4 h-4" /> Logout
                  </button>
                ) : (
                  <button type="button" onClick={() => { handleSignIn(); setMobileMenuOpen(false); }} className="block w-full text-center py-2.5 bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-500 hover:to-cyan-400 text-white rounded-lg text-sm font-medium transition-all">
                    Sign In
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default SharedHeader;
